import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import useTagManager from '../../../hooks/use-tag-manager'
import {selectVisiblePromotions} from '../../../utils/product-utils'

const Promotions = ({
    product,
    isplp = false,
    dataLayerLoaded = false,
    selectedVariant = null,
    isBloomReachSearch = false
}) => {
    const styles = useMultiStyleConfig('ProductTileStyles')
    const promoElements = useRef({})
    const tagManager = useTagManager()
    // Shared with the GTM item_badge param so the tracked promos match the rendered ones
    const visiblePromos = selectVisiblePromotions(product, selectedVariant, isBloomReachSearch)

    useEffect(() => {
        if (!visiblePromos.length) return
        if (typeof window === 'undefined') return

        window.intersectedPromos ??= []

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const promoId = entry.target.getAttribute('data-id')
                    if (
                        !entry.isIntersecting ||
                        !promoId ||
                        window.intersectedPromos.includes(promoId)
                    )
                        return

                    const promo = visiblePromos.find((p) => p.id === promoId)
                    if (!promo || !isplp || !dataLayerLoaded) return // We'll trigger this only on PLP

                    window.__dataLayerLoaded = true
                    tagManager.promoMessageImpression({
                        id: promoId,
                        name: promo.name,
                        creative: `PLP ${promo.backgroundColor || 'green'} badge`,
                        position: 'plp'
                    })
                    window.__dataLayerLoaded = false

                    window.intersectedPromos.push(promoId)
                })
            },
            {threshold: 0}
        )

        visiblePromos.forEach((promo) => {
            const el = promoElements.current[promo.id]
            if (el) observer.observe(el)
        })

        return () => {
            visiblePromos.forEach((promo) => {
                const el = promoElements.current[promo.id]
                if (el) observer.unobserve(el)
            })
        }
    }, [visiblePromos, tagManager])

    if (!visiblePromos.length) return null

    return (
        <>
            {visiblePromos.map((promo, index) => (
                <Text
                    key={promo.id || index}
                    data-id={promo.id}
                    className="tile-promo-text"
                    data-type={promo.promotionClass}
                    {...styles.promotionsWrapper?.promo}
                    color={promo.textColor || styles.promotionsWrapper?.promo?.color}
                    background={
                        promo.backgroundColor || styles.promotionsWrapper?.promo?.background
                    }
                    ref={(el) => {
                        if (el && promo.id) promoElements.current[promo.id] = el
                    }}
                >
                    {promo.name}
                </Text>
            ))}
        </>
    )
}

Promotions.propTypes = {
    isplp: PropTypes.any,
    selectedVariant: PropTypes.any,
    dataLayerLoaded: PropTypes.bool,
    isBloomReachSearch: PropTypes.bool,
    product: PropTypes.shape({
        c_promotions: PropTypes.any,
        promotions: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string,
                c_textColor: PropTypes.string,
                c_backgroundColor: PropTypes.string
            })
        )
    })
}

export default Promotions
